import { useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useSelection } from '../../hooks/use-selection';
import { GenericTable, Column } from '../../components/generic/generic.table';

import { useMutation, useQuery } from '@apollo/client';
import { DATE_FORMAT } from '../../constants';
import { HairService } from '../../gql/graphql';
import { COPY_HAIR_SERVICES, DETELE_SERVICE, LIST_SERVICES } from '../../gql/queries';
import { useShopDetails } from '../../hooks/use-shop';
import { GET_USER_SHOP_ACCESS } from '../../components/login/shop.selector';

const columns: Column[] = [
	{ title: 'Name', key: 'name' },
	{ title: 'Duration', key: 'duration_minutes' },
	{ title: 'Price', key: 'price' },
	{ title: 'Created', key: 'createdAt', formatter: (column: Column, data: any) => { return format(new Date(data[column.key]), DATE_FORMAT); } },
];

const useCustomerIds = (customers: any) => {
	return useMemo(
		() => {
			return customers.map((customer: any) => customer.id);
		},
		[customers]
	);
};
function HairServiceList() {
	const navigate = useNavigate();
	const { shopId, locationId, profile } = useShopDetails();
	const { data, loading, refetch } = useQuery(LIST_SERVICES, { variables: { shopId, locationId } });

	// Copy from another branch (D12): offered when the shop has one. The
	// selector's query already lists every branch the user may reach.
	const { data: accessData } = useQuery(GET_USER_SHOP_ACCESS);
	const otherLocations: Array<{ _id: string; name?: string | null; address?: { city?: string | null } | null }> = useMemo(() => {
		const shop = accessData?.getUserShopAccess?.find((item: { _id: string }) => item._id === shopId);
		return (shop?.location ?? []).filter((location: { _id: string }) => location._id !== locationId);
	}, [accessData, shopId, locationId]);
	const [copyOpen, setCopyOpen] = useState(false);
	const [copyFrom, setCopyFrom] = useState('');
	const [copyServices, { loading: copying }] = useMutation(COPY_HAIR_SERVICES);
	const handleCopy = useCallback(async () => {
		if (!copyFrom) return;
		try {
			const result = await copyServices({ variables: { shopId, fromLocationId: copyFrom, toLocationId: locationId } });
			const { copied, skipped } = result.data?.copyHairServices ?? { copied: 0, skipped: [] };
			toast.success(`Copied ${copied} ${copied === 1 ? 'service' : 'services'}${skipped.length ? `; skipped ${skipped.length} already here: ${skipped.join(', ')}` : ''}`);
			setCopyOpen(false);
			setCopyFrom('');
		} catch (error: any) {
			toast.error(error?.message ?? 'Could not copy the services');
			return;
		}
		// The copy is done whatever happens to the refresh; say so rather than report a failed copy.
		refetch().catch(() => toast.warn('Copied, but the list could not be refreshed; reload the page.'));
	}, [copyFrom, copyServices, shopId, locationId, refetch]);

	const hairservices: HairService[] = useMemo(
		() => {
			return data?.hairservices ? data?.hairservices : []
		},
		[data]
	);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	// const customers = useCustomers(page, rowsPerPage, data?.listCustomer);
	const customersIds = useCustomerIds(hairservices);
	const customersSelection = useSelection(customersIds);

	const handlePageChange = useCallback(
		(event: any, value: any) => {
			setPage(value);
		},
		[]
	);

	const handleRowsPerPageChange = useCallback(
		(event: any) => {
			setRowsPerPage(event.target.value);
		},
		[]
	);

	const [deleteService] = useMutation(DETELE_SERVICE);
	const handleDeleteOne = useCallback(
		async (service: HairService) => {
			await deleteService({ variables: { serviceId: service._id } });
			toast.success(`Deleted ${service.name} successfully.`);
			await refetch();
		},
		// eslint-disable-next-line
		[]
	);

	const handleAddEdit = useCallback(
		(service?: HairService) => {
			if (service?._id) {
				// Edit mode - navigate to edit page
				navigate(`/dashboard/services/${service._id}/edit`);
			} else {
				// Add mode - navigate to new service page
				navigate('/dashboard/services/new');
			}
		},
		[navigate]
	);

	return (
		<>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 8
				}}
			>
				<Container maxWidth="xl">
					{otherLocations.length > 0 && (
						<Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
							<Button variant="outlined" onClick={() => setCopyOpen(true)}>Copy from another location</Button>
						</Stack>
					)}
					<Dialog open={copyOpen} onClose={() => !copying && setCopyOpen(false)} fullWidth maxWidth="xs">
						<DialogTitle>Copy {profile.servicesLabel.toLowerCase()} from another location</DialogTitle>
						<DialogContent>
							<DialogContentText sx={{ mb: 2 }}>
								Each one is copied with its price, duration, tax and commission. A name already here is skipped, so this can be run again safely.
							</DialogContentText>
							<TextField select fullWidth label="Copy from" value={copyFrom} onChange={(event) => setCopyFrom(event.target.value)}>
								{otherLocations.map((location) => (
									<MenuItem key={location._id} value={location._id}>
										{location.name || location.address?.city || location._id}
									</MenuItem>
								))}
							</TextField>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setCopyOpen(false)} disabled={copying}>Cancel</Button>
							<Button variant="contained" onClick={handleCopy} disabled={!copyFrom || copying}>{copying ? 'Copying…' : 'Copy'}</Button>
						</DialogActions>
					</Dialog>
					<GenericTable
						title={profile.servicesLabel}
						data={hairservices}
						columns={columns}
						onDeselectAll={customersSelection.handleDeselectAll}
						onDeselectOne={customersSelection.handleDeselectOne}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						onSelectAll={customersSelection.handleSelectAll}
						onSelectOne={customersSelection.handleSelectOne}
						onDeleteOne={handleDeleteOne}
						onAddEdit={handleAddEdit}
						page={page}
						rowsPerPage={rowsPerPage}
						selected={customersSelection.selected}
						refetch={refetch}
						loading={loading}
					/>
				</Container>
			</Box>
		</>
	);
}

export default HairServiceList;
